import { normalizeVraiFaux } from '../normalizers/normalizeVraiFaux';
import PrintEnonce from '../helpers/PrintEnonce';
import { CONSIGNES } from '../consignes';
import { CONSIGNES_CAHIER, consigneTableLibre, tableEnListe } from '../helpers/saisiePolicy';
import { isVraiFaux, indexFigurePartagee } from '../helpers/groupQuestions';
import { VraiFauxBody, TableLibreBody } from './PrintVraiFaux';

/**
 * Renderer print d'un BLOC d'exercices-tableaux groupé (V/F ou tableaux
 * génériques colonnesLibres…) : un seul header "Exercice N", une seule
 * consigne, puis une liste numérotée 1./2./3.
 * Le corps (tableau à cocher / liste) suit les mêmes règles que
 * PrintVraiFaux (VraiFauxBody / TableLibreBody partagés).
 *
 * Une sous-question passe en LISTE (TableLibreBody) en réponse libre
 * (`reponseLibre`, axe format) ou en mode cahier ; un tableau d'association
 * garde ses cases dans les deux cas. La consigne de tête suit : celle du
 * tableau (par libellés de colonnes) si tout est en liste, mixte sinon.
 */
const CONSIGNE_MIXTE = 'Réponds à chaque question : écris la réponse, ou coche dans le tableau quand il est proposé.';
const CONSIGNE_MIXTE_CAHIER = 'Réponds à chaque question sur ton cahier : écris la réponse, ou recopie et complète le tableau quand il est proposé.';

const consigneDeTete = (items, infos, enCahier, reponseLibre) => {
    const consignes = enCahier ? CONSIGNES_CAHIER : CONSIGNES;
    const nbListes = infos.filter((x) => x.enListe).length;
    if (nbListes === 0) {
        return infos.some((x) => x.estVF) ? consignes.vf : consignes.table;
    }
    if (nbListes < items.length) return enCahier ? CONSIGNE_MIXTE_CAHIER : CONSIGNE_MIXTE;
    // Tout est en liste : consigne du 1er tableau (un groupe vient du même
    // exerciseur → colonnes homogènes) ; sinon formulation générique.
    const colonnesRef = infos[0].colonnes;
    const homogene = infos.every((x) => x.colonnes.join('|') === colonnesRef.join('|'));
    if (homogene) return consigneTableLibre(colonnesRef, enCahier, { justifier: reponseLibre });
    return enCahier
        ? 'Sur ton cahier, écris pour chaque ligne la bonne réponse.'
        : 'Pour chaque ligne, écris la bonne réponse.';
};

const PrintVraiFauxGroup = ({ exNum, items, saisieMode = 'sur_la_feuille', reponseLibre = false, figurePartagee = null }) => {
    const enCahier = saisieMode === 'sur_le_cahier';
    const infos = items.map(({ payload }) => ({
        estVF: isVraiFaux(payload),
        enListe: tableEnListe(payload, saisieMode, reponseLibre),
        colonnes: normalizeVraiFaux(payload).colonnes,
    }));
    return (
        <div className="ep-exo ep-vf-group">
            <span className="ep-exo-num">{exNum}</span>
            <p className="ep-exo-consigne">{consigneDeTete(items, infos, enCahier, reponseLibre)}</p>
            {figurePartagee?.element ? (
                <PrintEnonce className="ep-figgroup-figure" texte="" elements={[figurePartagee.element]} />
            ) : null}
            <ol className="ep-qcm-group-list">
                {items.map(({ payload, originalIndex }, idx) => {
                    const { question, lignes, colonnes } = normalizeVraiFaux(payload, idx + 1);
                    const { estVF, enListe } = infos[idx];
                    const skipIdx = indexFigurePartagee(payload, figurePartagee?.cle);
                    return (
                        <li key={payload?.id ?? `sub-${originalIndex}`} className="ep-qcm-group-item">
                            {question && (
                                <PrintEnonce
                                    className="ep-qcm-group-question"
                                    texte={question}
                                    elements={payload?.elements_additionnels?.elements}
                                    skipTables
                                    skipIndices={skipIdx >= 0 ? [skipIdx] : null}
                                />
                            )}
                            {enListe ? (
                                <TableLibreBody payload={payload} enCahier={enCahier} />
                            ) : (
                                <VraiFauxBody
                                    lignes={lignes}
                                    colonnes={colonnes}
                                    estVF={estVF}
                                    enCahier={enCahier}
                                />
                            )}
                        </li>
                    );
                })}
            </ol>
        </div>
    );
};

export default PrintVraiFauxGroup;
